import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import { categories, articles } from '@/mocks/categories';

export default function Categories() {
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || categories[0].id;
  const [activeTab, setActiveTab] = useState(initialTab);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    setVisible(true);
  }, []);

  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam && categories.some((c) => c.id === tabParam)) {
      setActiveTab(tabParam);
    }
  }, [searchParams]);

  const filteredArticles = articles.filter((a) => a.categoryId === activeTab);
  const activeCategory = categories.find((c) => c.id === activeTab);

  const webPageJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: '分类导航 - KAIYUN导航',
    description: '浏览 kaiyun.con 相关内容的分类导航页面，涵盖访问指南、平台介绍、常见问题、使用教程及最新动态。',
    url: 'https://kaiyun-navi.com/categories',
    isPartOf: {
      '@type': 'WebSite',
      name: 'KAIYUN导航',
      url: 'https://kaiyun-navi.com',
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(webPageJsonLd) }}
      />

      <SEOHead
        title="分类导航 | kaiyun.con访问指南 - KAIYUN导航"
        description="浏览 kaiyun.con 相关内容的分类导航页面，涵盖访问指南、平台介绍、常见问题、使用教程及最新动态，帮助您快速找到所需信息。"
        keywords="kaiyun.con分类,kaiyun.con导航,kaiyun.con访问指南,kaiyun.con常见问题,kaiyun.con使用教程"
        canonicalUrl="https://kaiyun-navi.com/categories"
      />

      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20">
          {/* Header */}
          <section className="bg-background-50 border-b border-background-200">
            <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-12">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: '分类导航' },
                ]}
              />
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mt-4 font-[var(--font-heading)]">
                分类导航
              </h1>
              <p className="text-sm md:text-base text-foreground-600 mt-2">
                选择您感兴趣的内容分类，浏览相关的导航指引与文章
              </p>
            </div>
          </section>

          {/* Tab Switcher */}
          <section className="bg-background-50 border-b border-background-200 sticky top-16 z-30">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="flex items-center gap-1 py-3 overflow-x-auto scrollbar-none">
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => setActiveTab(cat.id)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all cursor-pointer ${
                      activeTab === cat.id
                        ? 'bg-primary-500 text-background-50'
                        : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-100'
                    }`}
                  >
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className={`${cat.icon} text-sm`}></i>
                    </div>
                    {cat.name}
                  </button>
                ))}
              </div>
            </div>
          </section>

          {/* Category Description */}
          <section className="bg-background-100 py-6 md:py-8">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div
                className={`transition-all duration-500 ${
                  visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                }`}
                key={activeTab}
              >
                <h2 className="text-lg md:text-xl font-semibold text-foreground-900 font-[var(--font-heading)]">
                  {activeCategory?.name}
                </h2>
                <p className="text-sm md:text-base text-foreground-600 mt-2 max-w-3xl">
                  {activeCategory?.description}
                </p>
              </div>
            </div>
          </section>

          {/* Articles List */}
          <section className="py-8 md:py-12 bg-background-50">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {filteredArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/articles/${article.id}`}
                    className="group p-5 bg-background-50 border border-background-200 rounded-lg hover:border-primary-200 transition-all cursor-pointer no-underline"
                  >
                    <div className="flex items-center gap-2 mb-3">
                      <span className="px-2 py-0.5 bg-accent-100 text-accent-700 text-xs rounded-full font-medium">
                        {activeCategory?.name}
                      </span>
                      <span className="text-xs text-foreground-400">{article.readTime}</span>
                    </div>
                    <h3 className="text-sm md:text-base font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors mb-2 line-clamp-2">
                      {article.title}
                    </h3>
                    <p className="text-xs md:text-sm text-foreground-500 line-clamp-2 mb-3">
                      {article.summary}
                    </p>
                    <div className="flex items-center gap-2 text-xs text-foreground-400">
                      <i className="ri-calendar-line"></i>
                      <span>{article.publishDate}</span>
                    </div>
                  </Link>
                ))}
              </div>
              {filteredArticles.length === 0 && (
                <div className="text-center py-12">
                  <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-background-100 rounded-full">
                    <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                  </div>
                  <p className="text-foreground-500">该分类暂无文章</p>
                </div>
              )}
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}